import React, { useState, useEffect, useRef } from "react";
import { FallbackProps } from "react-error-boundary";

export function ErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  const [showDetails, setShowDetails] = useState(false);
  const [copied, setCopied] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState<Record<string, string>>({});
  const detailsRef = useRef<HTMLPreElement | null>(null);

  useEffect(() => {
    const info: Record<string, string> = {
      platform: navigator.platform,
      userAgent: navigator.userAgent,
      language: navigator.language,
      online: navigator.onLine ? "Yes" : "No",
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      windowSize: `${window.innerWidth}x${window.innerHeight}`,
      colorDepth: `${window.screen.colorDepth} bits`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      cookiesEnabled: navigator.cookieEnabled ? "Yes" : "No",
    };

    const ua = navigator.userAgent;
    if (/Android/i.test(ua)) info.deviceType = "Android";
    else if (/iPhone|iPad|iPod/i.test(ua)) info.deviceType = "iOS";
    else if (/Mac/i.test(ua)) info.deviceType = "Mac";
    else if (/Windows/i.test(ua)) info.deviceType = "Windows";
    else if (/Linux/i.test(ua)) info.deviceType = "Linux";
    else info.deviceType = "Unknown";

    if (/Chrome/i.test(ua)) info.browser = "Chrome";
    else if (/Safari/i.test(ua)) info.browser = "Safari";
    else if (/Firefox/i.test(ua)) info.browser = "Firefox";
    else if (/Edge/i.test(ua)) info.browser = "Edge";
    else if (/Opera/i.test(ua)) info.browser = "Opera";
    else info.browser = "Unknown";

    setDeviceInfo(info);
  }, []);

  const errorContent = `${error.message}\n\n${error.stack}`;

  const diagnosticData =
    Object.entries(deviceInfo)
      .map(([key, value]) => `${key}: ${value}`)
      .join("\n") + `\n\n${errorContent}`;

  const handleCopy = async () => {
    if (!diagnosticData) return;

    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(diagnosticData);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        return;
      } catch (err) {
        console.error("Clipboard write failed:", err);
        alert(
          "Unable to copy automatically. Please select and copy the error details manually."
        );
      }
    } else {
      alert(
        "Copying to clipboard is not supported in this context. Please select and copy the error details manually."
      );
    }
  };

  /** Selects all text in the error details block */
  const handleSelectAll = () => {
    if (detailsRef.current) {
      const range = document.createRange();
      range.selectNodeContents(detailsRef.current);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  };

  return (
    <div className="container mt-5">
      <div
        className="card shadow-sm border-0 mx-auto"
        style={{ maxWidth: 600 }}
      >
        <div className="card-body text-center p-4">
          <h4 className="text-danger mb-3 fw-semibold">
            Oops, something went wrong 😕
          </h4>
          <p className="text-muted mb-4">
            An unexpected error occurred. You can return to the home screen to
            continue using the app.
          </p>

          <div className="d-flex flex-column align-items-center gap-2 mb-3">
            <button
              className="btn btn-primary px-4"
              onClick={resetErrorBoundary}
            >
              Back to Home
            </button>

            <button
              className={`btn ${
                copied ? "btn-success" : "btn-outline-secondary"
              } px-4`}
              onClick={handleCopy}
            >
              {copied ? "✅ Error details copied!" : "📋 Copy error details"}
            </button>
          </div>

          <div>
            <button
              className="btn btn-link text-decoration-none"
              onClick={() => setShowDetails((prev) => !prev)}
            >
              {showDetails ? "Hide error details" : "Show error details"}
            </button>

            {showDetails && (
              <div className="mt-3">
                <pre
                  ref={detailsRef}
                  className="text-start p-3 bg-light border rounded small"
                  style={{
                    maxHeight: 300,
                    overflowY: "auto",
                    whiteSpace: "pre-wrap",
                    textAlign: "left",
                    userSelect: "text",
                  }}
                >
                  <code>
                    {Object.entries(deviceInfo)
                      .map(([key, value]) => `${key}: ${value}`)
                      .join("\n")}
                    {"\n\n"}
                    {errorContent}
                  </code>
                </pre>

                <div className="text-end mt-2">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={handleSelectAll}
                  >
                    Select all
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
